import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  fetchAdmin,
  postAdmin,
  putAdmin,
  safeJsonParse,
  toErrorMessage,
} from '../../admin/api';
import GrantExpiryChip from '../../admin/components/GrantExpiryChip';
import PermissionGate from '../../admin/components/PermissionGate';
import { AdminConfirmationDialog, AdminDataTable, AdminDetailPanel, AdminErrorState, AdminLoadingState, AdminNoDataState, AdminScreenLayout, AdminStatusChip, AdminTimeline } from '../../sections/admin/shared';
import TransactionsView from '../../sections/admin/transactions/TransactionsView';
import { PATH_DASHBOARD } from '../../routes/paths';
import { fDateTime } from '../../utils/formatTime';

const breadcrumbsFor = (title) => [{ name: 'Admin', href: PATH_DASHBOARD.root }, { name: title }];

function StatCard({ title, value, caption, status }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
            {status ? <AdminStatusChip status={status} /> : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function RequestState({ loading, error, children }) {
  if (loading) {
    return <AdminLoadingState rows={4} />;
  }

  if (error) {
    return <AdminErrorState title="Request failed" message={error} />;
  }

  return children;
}

function useAsyncState(initialData = null) {
  const [state, setState] = useState({ loading: false, error: null, data: initialData });

  return {
    state,
    setLoading: () => setState((prev) => ({ ...prev, loading: true, error: null })),
    setData: (data) => setState({ loading: false, error: null, data }),
    setError: (error) => setState((prev) => ({ ...prev, loading: false, error })),
  };
}

function DashboardView() {
  const risk = useAsyncState([]);
  const settlement = useAsyncState(null);
  const queues = useAsyncState(null);
  const sos = useAsyncState([]);
  const services = useAsyncState({ providers: [], incidents: [] });
  const controlPlane = useAsyncState(null);

  useEffect(() => {
    const load = async () => {
      risk.setLoading();
      settlement.setLoading();
      queues.setLoading();
      sos.setLoading();
      services.setLoading();
      controlPlane.setLoading();

      try {
        const [riskData, settlementData, queueData, sosData, serviceData, controlData] = await Promise.all([
          fetchAdmin('/risk/summary'),
          fetchAdmin('/settlement/overview'),
          fetchAdmin('/ops/queues/overview'),
          fetchAdmin('/sos/active'),
          fetchAdmin('/services/overview'),
          fetchAdmin('/control_plane/status'),
        ]);

        risk.setData(riskData.summary || []);
        settlement.setData(settlementData);
        queues.setData(queueData.overview || {});
        sos.setData(sosData.signals || []);
        services.setData(serviceData);
        controlPlane.setData(controlData);
      } catch (error) {
        const message = toErrorMessage(error);
        risk.setError(message);
        settlement.setError(message);
        queues.setError(message);
        sos.setError(message);
        services.setError(message);
        controlPlane.setError(message);
      }
    };

    load();
  }, []);

  const riskHits = (risk.state.data || []).reduce((sum, item) => sum + Number(item.review_hits || 0), 0);

  return (
    <AdminScreenLayout title="Dashboard" breadcrumbs={breadcrumbsFor('Dashboard')}>
      <RequestState loading={risk.state.loading || settlement.state.loading || queues.state.loading || sos.state.loading || services.state.loading || controlPlane.state.loading} error={risk.state.error || settlement.state.error || queues.state.error || sos.state.error || services.state.error || controlPlane.state.error}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Policy Review Hits" value={riskHits} caption="Aggregated from risk summary" />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Settlement Queue" value={settlement.state.data?.queue || 0} caption="Queued settlement watches" />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Failed Jobs" value={settlement.state.data?.jobs_failed || 0} caption="Settlement-linked job failures" />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Outbox Pending" value={queues.state.data?.outbox_pending || 0} caption="Dispatch intent not yet queued" />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Stalled Settlement" value={settlement.state.data?.stalled || 0} caption="Requires intervention" />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Active SOS" value={(sos.state.data || []).length} caption="Current active safety signals" />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Shadow Mode" value={controlPlane.state.data?.shadow_mode ? 'On' : 'Off'} caption="Control plane rollout mode" status={controlPlane.state.data?.shadow_mode ? 'shadow' : 'resolved'} />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <StatCard title="Enforcement Mode" value={controlPlane.state.data?.enforcement_mode ? 'On' : 'Off'} caption="Live policy enforcement" status={controlPlane.state.data?.enforcement_mode ? 'enforced' : 'pending'} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <AdminDataTable
              title="Provider Overview"
              description="Live service provider performance rows from the backend overview route."
              columns={[
                { field: 'provider', headerName: 'Provider' },
                { field: 'successRate', headerName: 'Success Rate' },
                { field: 'failureRate', headerName: 'Failure Rate' },
                { field: 'latency', headerName: 'P95 Latency' },
              ]}
              rows={(services.state.data?.providers || []).map((row) => ({
                id: row.id,
                provider: row.provider,
                successRate: `${row.success_rate}%`,
                failureRate: `${row.failure_rate}%`,
                latency: `${row.p95_latency_ms} ms`,
              }))}
              emptyTitle="No provider overview rows"
              emptyDescription="The service overview endpoint returned no provider metrics."
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <AdminDataTable
              title="Open Incidents"
              description="Recent incidents published by the service overview route."
              columns={[
                { field: 'provider', headerName: 'Provider' },
                { field: 'status', headerName: 'Status' },
                { field: 'severity', headerName: 'Severity' },
              ]}
              rows={(services.state.data?.incidents || []).map((row) => ({
                id: row.id,
                provider: row.provider,
                status: row.status,
                severity: row.severity,
              }))}
              emptyTitle="No incidents"
              emptyDescription="The backend returned no incidents for the overview payload."
            />
          </Grid>
        </Grid>
      </RequestState>
    </AdminScreenLayout>
  );
}

function AccessControlView() {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/requests')
    ? 'requests'
    : pathname.includes('/grants')
      ? 'grants'
      : pathname.includes('/audit')
        ? 'audit'
        : 'staff';
  const staff = useAsyncState([]);
  const detail = useAsyncState(null);
  const requests = useAsyncState([]);
  const grants = useAsyncState([]);
  const audit = useAsyncState([]);
  const roles = useAsyncState([]);
  const permissions = useAsyncState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [editForm, setEditForm] = useState({
    role_code: '',
    timezone: '',
    work_days: '',
    shift_start_time: '',
    shift_end_time: '',
    allow_after_hours: 'false',
    status: '',
  });
  const [requestForm, setRequestForm] = useState({
    staff_id: '',
    permission_code: '',
    resource_type: '',
    resource_id: '',
    reason: '',
  });
  const [pendingAction, setPendingAction] = useState(null);

  const loadCommon = async () => {
    const [staffData, roleData, permissionData] = await Promise.all([
      fetchAdmin('/access/staff'),
      fetchAdmin('/access/roles'),
      fetchAdmin('/access/permissions'),
    ]);

    staff.setData(staffData.staff || []);
    roles.setData(roleData.roles || []);
    permissions.setData(permissionData.permissions || []);
  };

  useEffect(() => {
    const load = async () => {
      staff.setLoading();
      roles.setLoading();
      permissions.setLoading();
      try {
        await loadCommon();
      } catch (error) {
        const message = toErrorMessage(error);
        staff.setError(message);
        roles.setError(message);
        permissions.setError(message);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (!selectedStaffId) {
      return;
    }

    const loadDetail = async () => {
      detail.setLoading();
      try {
        const response = await fetchAdmin(`/access/staff/${selectedStaffId}`);
        detail.setData(response);
        setEditForm({
          role_code: response.profile?.role_code || '',
          timezone: response.profile?.timezone || '',
          work_days: (safeJsonParse(response.profile?.work_days_json, []) || []).join(','),
          shift_start_time: response.profile?.shift_start_time || '',
          shift_end_time: response.profile?.shift_end_time || '',
          allow_after_hours: String(Boolean(response.profile?.allow_after_hours)),
          status: response.profile?.status || '',
        });
      } catch (error) {
        detail.setError(toErrorMessage(error));
      }
    };

    loadDetail();
  }, [selectedStaffId]);

  useEffect(() => {
    if (section !== 'requests') {
      return;
    }

    const load = async () => {
      requests.setLoading();
      try {
        const response = await fetchAdmin('/access/requests');
        requests.setData(response.requests || []);
      } catch (error) {
        requests.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section]);

  useEffect(() => {
    if (section !== 'grants') {
      return;
    }

    const load = async () => {
      grants.setLoading();
      try {
        const response = await fetchAdmin('/access/grants');
        grants.setData(response.grants || []);
      } catch (error) {
        grants.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section]);

  useEffect(() => {
    if (section !== 'audit') {
      return;
    }

    const load = async () => {
      audit.setLoading();
      try {
        const response = await fetchAdmin('/access/audit');
        audit.setData(response.items || []);
      } catch (error) {
        audit.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section]);

  const saveStaffProfile = async () => {
    try {
      await putAdmin(`/access/staff/${selectedStaffId}`, {
        role_code: editForm.role_code,
        timezone: editForm.timezone,
        work_days: editForm.work_days.split(',').map((item) => item.trim()).filter(Boolean),
        shift_start_time: editForm.shift_start_time,
        shift_end_time: editForm.shift_end_time,
        allow_after_hours: editForm.allow_after_hours === 'true',
        status: editForm.status,
      });
      enqueueSnackbar('Staff profile updated', { variant: 'success' });
      const refreshed = await fetchAdmin(`/access/staff/${selectedStaffId}`);
      detail.setData(refreshed);
      await loadCommon();
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const createAccessRequest = async () => {
    try {
      await postAdmin('/access/requests', requestForm);
      enqueueSnackbar('Access request created', { variant: 'success' });
      setRequestForm({ staff_id: '', permission_code: '', resource_type: '', resource_id: '', reason: '' });
      const refreshed = await fetchAdmin('/access/requests');
      requests.setData(refreshed.requests || []);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const completeAction = async () => {
    if (!pendingAction) {
      return;
    }

    try {
      await postAdmin(pendingAction.path, pendingAction.method === 'post' ? pendingAction.payload : undefined);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });

      if (section === 'requests') {
        const refreshed = await fetchAdmin('/access/requests');
        requests.setData(refreshed.requests || []);
      }

      if (section === 'grants') {
        const refreshed = await fetchAdmin('/access/grants');
        grants.setData(refreshed.grants || []);
      }
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  };

  const title = section === 'requests' ? 'Access Requests' : section === 'grants' ? 'Active Grants' : section === 'audit' ? 'Access Audit' : 'Access Control Staff';

  return (
    <AdminScreenLayout title={title} breadcrumbs={breadcrumbsFor('Access Control')}>
      {section === 'staff' ? (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={7}>
            <RequestState loading={staff.state.loading} error={staff.state.error}>
              <AdminDataTable
                title="Staff Profiles"
                description="Governed staff profiles from the access layer."
                columns={[
                  { field: 'staffId', headerName: 'Staff ID' },
                  { field: 'role', headerName: 'Role' },
                  { field: 'status', headerName: 'Status' },
                  {
                    field: 'select',
                    headerName: 'Select',
                    render: (row) => (
                      <Button size="small" onClick={() => setSelectedStaffId(row.staffId)}>
                        Open
                      </Button>
                    ),
                  },
                ]}
                rows={(staff.state.data || []).map((row) => ({
                  id: row.id,
                  staffId: row.staff_id,
                  role: row.role_code,
                  status: row.status,
                }))}
                emptyTitle="No staff profiles"
                emptyDescription="The access staff endpoint returned no governed staff profiles."
              />
            </RequestState>
          </Grid>
          <Grid item xs={12} lg={5}>
            <RequestState loading={detail.state.loading && Boolean(selectedStaffId)} error={detail.state.error}>
              {selectedStaffId ? (
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6">Staff Profile Editor</Typography>
                      <TextField select label="Role" value={editForm.role_code} onChange={(event) => setEditForm((prev) => ({ ...prev, role_code: event.target.value }))}>
                        {(roles.state.data || []).map((role) => (
                          <MenuItem key={role.id} value={role.role_code}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField label="Timezone" value={editForm.timezone} onChange={(event) => setEditForm((prev) => ({ ...prev, timezone: event.target.value }))} />
                      <TextField label="Work days" helperText="Comma-separated, matching the contract format" value={editForm.work_days} onChange={(event) => setEditForm((prev) => ({ ...prev, work_days: event.target.value }))} />
                      <TextField label="Shift start time" value={editForm.shift_start_time} onChange={(event) => setEditForm((prev) => ({ ...prev, shift_start_time: event.target.value }))} />
                      <TextField label="Shift end time" value={editForm.shift_end_time} onChange={(event) => setEditForm((prev) => ({ ...prev, shift_end_time: event.target.value }))} />
                      <TextField select label="Allow after hours" value={editForm.allow_after_hours} onChange={(event) => setEditForm((prev) => ({ ...prev, allow_after_hours: event.target.value }))}>
                        <MenuItem value="false">No</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                      </TextField>
                      <TextField select label="Status" value={editForm.status} onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}>
                        <MenuItem value="active">active</MenuItem>
                        <MenuItem value="inactive">inactive</MenuItem>
                      </TextField>
                      <PermissionGate moduleName="access" permissionCode="access.manage">
                        <LoadingButton variant="contained" onClick={saveStaffProfile}>
                          Save Staff Profile
                        </LoadingButton>
                      </PermissionGate>
                      <Divider />
                      <AdminDetailPanel
                        title="Session + Grant Detail"
                        items={[
                          { label: 'Current session', value: detail.state.data?.sessions?.[0]?.session_id || 'N/A' },
                          { label: 'Trust level', value: detail.state.data?.sessions?.[0]?.trust_level || 'N/A' },
                          { label: 'Grant expiry', value: detail.state.data?.grants?.[0]?.expires_at || 'N/A' },
                        ]}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <AdminNoDataState title="Select a staff profile" description="Choose a governed staff profile from the list to load sessions, grants, and editable profile fields." />
              )}
            </RequestState>
          </Grid>
        </Grid>
      ) : null}

      {section === 'requests' ? (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={7}>
            <RequestState loading={requests.state.loading} error={requests.state.error}>
              <AdminDataTable
                title="Access Requests"
                description="Pending and reviewed elevated-access requests."
                columns={[
                  { field: 'staffId', headerName: 'Staff ID' },
                  { field: 'permission', headerName: 'Permission' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'resource', headerName: 'Resource' },
                  {
                    field: 'actions',
                    headerName: 'Actions',
                    render: (row) => (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => setPendingAction({ path: `/access/requests/${row.id}/approve`, success: 'Request approved' })}>
                          Approve
                        </Button>
                        <Button size="small" color="error" onClick={() => setPendingAction({ path: `/access/requests/${row.id}/reject`, success: 'Request rejected' })}>
                          Reject
                        </Button>
                      </Stack>
                    ),
                  },
                ]}
                rows={(requests.state.data || []).map((row) => ({
                  id: row.id,
                  staffId: row.staff_id,
                  permission: row.permission_code,
                  status: row.status,
                  resource: `${row.resource_type}:${row.resource_id}`,
                }))}
                emptyTitle="No access requests"
                emptyDescription="The access requests endpoint returned no request rows."
              />
            </RequestState>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Request Access</Typography>
                  <TextField label="Staff ID" value={requestForm.staff_id} onChange={(event) => setRequestForm((prev) => ({ ...prev, staff_id: event.target.value }))} />
                  <TextField select label="Permission" value={requestForm.permission_code} onChange={(event) => setRequestForm((prev) => ({ ...prev, permission_code: event.target.value }))}>
                    {(permissions.state.data || []).map((permission) => (
                      <MenuItem key={permission.id} value={permission.permission_code}>
                        {permission.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Resource type" value={requestForm.resource_type} onChange={(event) => setRequestForm((prev) => ({ ...prev, resource_type: event.target.value }))} />
                  <TextField label="Resource ID" value={requestForm.resource_id} onChange={(event) => setRequestForm((prev) => ({ ...prev, resource_id: event.target.value }))} />
                  <TextField multiline minRows={3} label="Reason" value={requestForm.reason} onChange={(event) => setRequestForm((prev) => ({ ...prev, reason: event.target.value }))} />
                  <LoadingButton variant="contained" onClick={createAccessRequest}>
                    Submit Request
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {section === 'grants' ? (
        <RequestState loading={grants.state.loading} error={grants.state.error}>
          <AdminDataTable
            title="Elevated Grants"
            description="Currently issued elevated grants and their expiry windows."
            columns={[
              { field: 'staffId', headerName: 'Staff ID' },
              { field: 'permission', headerName: 'Permission' },
              { field: 'status', headerName: 'Status' },
              {
                field: 'expires',
                headerName: 'Expiry',
                render: (row) => <GrantExpiryChip expiresAt={row.expiresAt} />,
              },
              {
                field: 'actions',
                headerName: 'Actions',
                render: (row) => (
                  <Button size="small" color="error" onClick={() => setPendingAction({ path: `/access/grants/${row.id}/revoke`, success: 'Grant revoked' })}>
                    Revoke
                  </Button>
                ),
              },
            ]}
            rows={(grants.state.data || []).map((row) => ({
              id: row.id,
              staffId: row.staff_id,
              permission: row.permission_code,
              status: row.status,
              expiresAt: row.expires_at,
            }))}
            emptyTitle="No grants"
            emptyDescription="The grants endpoint returned no elevated grant records."
          />
        </RequestState>
      ) : null}

      {section === 'audit' ? (
        <RequestState loading={audit.state.loading} error={audit.state.error}>
          <AdminDataTable
            title="Access Audit"
            description="Governance audit records emitted by backend access enforcement."
            columns={[
              { field: 'staffId', headerName: 'Staff ID' },
              { field: 'permission', headerName: 'Permission' },
              { field: 'action', headerName: 'Action' },
              { field: 'result', headerName: 'Result' },
              { field: 'createdAt', headerName: 'Created' },
            ]}
            rows={(audit.state.data || []).map((row) => ({
              id: row.id,
              staffId: row.staff_id,
              permission: row.permission_code,
              action: row.action,
              result: row.result,
              createdAt: fDateTime(row.created_at),
            }))}
            emptyTitle="No audit records"
            emptyDescription="The access audit endpoint returned no audit activity rows."
          />
        </RequestState>
      ) : null}

      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm action"
        description="This action will be sent to the governed backend immediately."
        confirmLabel="Continue"
        onClose={() => setPendingAction(null)}
        onConfirm={completeAction}
      />
    </AdminScreenLayout>
  );
}

function RiskPolicyView() {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/decision-logs')
    ? 'logs'
    : pathname.includes('/thresholds')
      ? 'thresholds'
      : pathname.includes('/simulator')
        ? 'simulator'
        : 'summary';
  const summary = useAsyncState([]);
  const logs = useAsyncState([]);
  const thresholds = useAsyncState([]);
  const [filters, setFilters] = useState({
    page: 0,
    page_size: 50,
    tier_code: '',
    transaction_type: '',
    decision_code: '',
    requires_review: '',
    reference: '',
  });

  useEffect(() => {
    const loadSummary = async () => {
      summary.setLoading();
      try {
        const response = await fetchAdmin('/risk/summary');
        summary.setData(response.summary || []);
      } catch (error) {
        summary.setError(toErrorMessage(error));
      }
    };

    loadSummary();
  }, []);

  useEffect(() => {
    if (section !== 'logs') {
      return;
    }

    const loadLogs = async () => {
      logs.setLoading();
      try {
        const response = await fetchAdmin('/risk/decision_logs', filters);
        logs.setData(response.data || []);
      } catch (error) {
        logs.setError(toErrorMessage(error));
      }
    };

    loadLogs();
  }, [section, filters]);

  useEffect(() => {
    if (section !== 'thresholds') {
      return;
    }

    const loadThresholds = async () => {
      thresholds.setLoading();
      try {
        const response = await fetchAdmin('/risk/third_party_thresholds');
        thresholds.setData(response.thresholds || []);
      } catch (error) {
        thresholds.setError(toErrorMessage(error));
      }
    };

    loadThresholds();
  }, [section]);

  const saveThreshold = async (index, field, value) => {
    const updated = [...(thresholds.state.data || [])];
    updated[index] = { ...updated[index], [field]: value };
    thresholds.setData(updated);
  };

  const submitThresholds = async () => {
    try {
      await putAdmin('/risk/third_party_thresholds', {
        thresholds: (thresholds.state.data || []).map((row) => ({
          tier_code: row.tier_code,
          daily_limit: row.daily_limit,
          currency: row.currency,
          is_active: Boolean(row.is_active),
        })),
      });
      enqueueSnackbar('Thresholds updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <AdminScreenLayout title="Risk & Policy" breadcrumbs={breadcrumbsFor('Risk & Policy')}>
      {section === 'summary' ? (
        <RequestState loading={summary.state.loading} error={summary.state.error}>
          <Grid container spacing={3}>
            {(summary.state.data || []).map((row) => (
              <Grid item xs={12} md={6} xl={4} key={row.decision_scope}>
                <StatCard title={row.decision_scope} value={row.total} caption={`${row.review_hits} review hits`} />
              </Grid>
            ))}
          </Grid>
        </RequestState>
      ) : null}

      {section === 'logs' ? (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Transaction type" value={filters.transaction_type} onChange={(event) => setFilters((prev) => ({ ...prev, transaction_type: event.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Tier code" value={filters.tier_code} onChange={(event) => setFilters((prev) => ({ ...prev, tier_code: event.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Decision code" value={filters.decision_code} onChange={(event) => setFilters((prev) => ({ ...prev, decision_code: event.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Requires review" value={filters.requires_review} onChange={(event) => setFilters((prev) => ({ ...prev, requires_review: event.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Reference" value={filters.reference} onChange={(event) => setFilters((prev) => ({ ...prev, reference: event.target.value }))} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <RequestState loading={logs.state.loading} error={logs.state.error}>
            <AdminDataTable
              title="Decision Logs"
              description="Policy decisions with readable matched rules and context payload previews."
              columns={[
                { field: 'scope', headerName: 'Scope' },
                { field: 'reference', headerName: 'Reference' },
                { field: 'status', headerName: 'Status' },
                { field: 'decision', headerName: 'Decision Code' },
                { field: 'context', headerName: 'Context' },
              ]}
              rows={(logs.state.data || []).map((row) => ({
                id: row.id,
                scope: row.decision_scope,
                reference: row.reference,
                status: row.requires_review ? 'awaiting review' : row.allowed ? 'resolved' : 'failed',
                decision: row.decision_code,
                context: JSON.stringify(safeJsonParse(row.context_payload, {})),
              }))}
              emptyTitle="No decision logs"
              emptyDescription="The risk decision log query returned no rows for the current filters."
            />
          </RequestState>
        </Stack>
      ) : null}

      {section === 'thresholds' ? (
        <RequestState loading={thresholds.state.loading} error={thresholds.state.error}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                {(thresholds.state.data || []).map((row, index) => (
                  <Grid container spacing={2} key={row.id}>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" label="Tier code" value={row.tier_code} disabled />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" label="Daily limit" value={row.daily_limit} onChange={(event) => saveThreshold(index, 'daily_limit', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth size="small" label="Currency" value={row.currency} onChange={(event) => saveThreshold(index, 'currency', event.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField select fullWidth size="small" label="Active" value={String(Boolean(row.is_active))} onChange={(event) => saveThreshold(index, 'is_active', event.target.value === 'true' ? 1 : 0)}>
                        <MenuItem value="true">true</MenuItem>
                        <MenuItem value="false">false</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                ))}
                <PermissionGate moduleName="risk" permissionCode="risk.manage">
                  <LoadingButton variant="contained" onClick={submitThresholds}>
                    Save Thresholds
                  </LoadingButton>
                </PermissionGate>
              </Stack>
            </CardContent>
          </Card>
        </RequestState>
      ) : null}

      {section === 'simulator' ? (
        <Alert severity="info">
          The `POST /v2/admin/risk/simulate` route is active, but its request payload contract is not yet documented in the contract file, so the simulator action remains intentionally unwired.
        </Alert>
      ) : null}
    </AdminScreenLayout>
  );
}

function UsersView() {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/primary-account')
    ? 'primary'
    : pathname.includes('/policy-decisions')
      ? 'policy'
      : pathname.includes('/beneficiaries')
        ? 'beneficiaries'
        : 'controls';
  const [userId, setUserId] = useState('');
  const [activeUserId, setActiveUserId] = useState('');
  const dataState = useAsyncState(null);
  const [controlForm, setControlForm] = useState({ control_code: '', reason: '', expires_at: '' });
  const [reviewForm, setReviewForm] = useState({ bank_account_id: '', review_notes: '' });

  const loadSection = async (targetUserId = activeUserId) => {
    if (!targetUserId) {
      return;
    }

    dataState.setLoading();
    try {
      const route =
        section === 'controls'
          ? `/users/${targetUserId}/controls`
          : section === 'primary'
            ? `/users/${targetUserId}/primary_account`
            : section === 'policy'
              ? `/users/${targetUserId}/policy_decisions`
              : `/users/${targetUserId}/beneficiaries`;
      const response = await fetchAdmin(route, section === 'policy' ? { page: 0, page_size: 50 } : undefined);
      dataState.setData(response);
    } catch (error) {
      dataState.setError(toErrorMessage(error));
    }
  };

  useEffect(() => {
    if (activeUserId) {
      loadSection(activeUserId);
    }
  }, [section]);

  const addControl = async () => {
    try {
      await postAdmin(`/users/${activeUserId}/controls`, controlForm);
      enqueueSnackbar('Control created', { variant: 'success' });
      loadSection(activeUserId);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const resolveControl = async (controlId) => {
    try {
      await postAdmin(`/users/controls/${controlId}/resolve`);
      enqueueSnackbar('Control resolved', { variant: 'success' });
      loadSection(activeUserId);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const triggerReview = async () => {
    try {
      await postAdmin(`/users/${activeUserId}/primary_account/review`, { bank_account_id: reviewForm.bank_account_id });
      enqueueSnackbar('Primary account review triggered', { variant: 'success' });
      loadSection(activeUserId);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const reviewPrimary = async (reviewId, action) => {
    try {
      await postAdmin(`/users/primary_account_reviews/${reviewId}/${action}`, { review_notes: reviewForm.review_notes });
      enqueueSnackbar(`Primary account ${action}d`, { variant: 'success' });
      loadSection(activeUserId);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <AdminScreenLayout title="Users" breadcrumbs={breadcrumbsFor('Users')}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="User ID" value={userId} onChange={(event) => setUserId(event.target.value)} sx={{ minWidth: 280 }} />
            <LoadingButton variant="contained" onClick={() => { setActiveUserId(userId); loadSection(userId); }}>
              Load User Module
            </LoadingButton>
          </Stack>
        </CardContent>
      </Card>
      {activeUserId ? (
        <RequestState loading={dataState.state.loading} error={dataState.state.error}>
          {section === 'controls' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <AdminDataTable
                  title="User Controls"
                  description="Active and resolved control rows for the selected user."
                  columns={[
                    { field: 'controlCode', headerName: 'Control' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'reason', headerName: 'Reason' },
                    { field: 'expiresAt', headerName: 'Expires' },
                    { field: 'actions', headerName: 'Actions', render: (row) => <Button size="small" onClick={() => resolveControl(row.id)}>Resolve</Button> },
                  ]}
                  rows={(dataState.state.data?.controls || []).map((row) => ({
                    id: row.id,
                    controlCode: row.control_code,
                    status: row.status,
                    reason: row.reason,
                    expiresAt: row.expires_at,
                  }))}
                  emptyTitle="No controls"
                  emptyDescription="The selected user has no documented control rows."
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6">Create Control</Typography>
                      <TextField label="Control code" value={controlForm.control_code} onChange={(event) => setControlForm((prev) => ({ ...prev, control_code: event.target.value }))} />
                      <TextField label="Reason" value={controlForm.reason} onChange={(event) => setControlForm((prev) => ({ ...prev, reason: event.target.value }))} />
                      <TextField label="Expires at" value={controlForm.expires_at} onChange={(event) => setControlForm((prev) => ({ ...prev, expires_at: event.target.value }))} />
                      <LoadingButton variant="contained" onClick={addControl}>
                        Add Control
                      </LoadingButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          {section === 'primary' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                <AdminDetailPanel
                  title="Primary Account"
                  items={[
                    { label: 'Bank', value: dataState.state.data?.primary_account?.bank?.bank_name || 'N/A' },
                    { label: 'Account name', value: dataState.state.data?.primary_account?.bank?.account_name || 'N/A' },
                    { label: 'Account number', value: dataState.state.data?.primary_account?.bank?.account_number || 'N/A' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} lg={8}>
                <AdminDataTable
                  title="Primary Account Reviews"
                  description="Documented primary-account review history and match outcomes."
                  columns={[
                    { field: 'reviewStatus', headerName: 'Review Status' },
                    { field: 'matchStatus', headerName: 'Match Status' },
                    { field: 'matchScore', headerName: 'Match Score' },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      render: (row) => (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={() => reviewPrimary(row.id, 'approve')}>Approve</Button>
                          <Button size="small" color="error" onClick={() => reviewPrimary(row.id, 'reject')}>Reject</Button>
                        </Stack>
                      ),
                    },
                  ]}
                  rows={(dataState.state.data?.reviews || []).map((row) => ({
                    id: row.id,
                    reviewStatus: row.review_status,
                    matchStatus: row.match_status,
                    matchScore: row.match_score,
                  }))}
                  emptyTitle="No primary account reviews"
                  emptyDescription="The selected user has no primary-account review history."
                />
                <Card variant="outlined" sx={{ mt: 3 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6">Trigger or Review Primary Account</Typography>
                      <TextField label="Bank account ID" value={reviewForm.bank_account_id} onChange={(event) => setReviewForm((prev) => ({ ...prev, bank_account_id: event.target.value }))} />
                      <TextField multiline minRows={3} label="Review notes" value={reviewForm.review_notes} onChange={(event) => setReviewForm((prev) => ({ ...prev, review_notes: event.target.value }))} />
                      <LoadingButton variant="contained" onClick={triggerReview}>
                        Trigger Review
                      </LoadingButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          {section === 'policy' ? (
            <AdminDataTable
              title="Policy Decisions"
              description="User-scoped policy decisions for the selected user."
              columns={[
                { field: 'reference', headerName: 'Reference' },
                { field: 'scope', headerName: 'Scope' },
                { field: 'status', headerName: 'Status' },
                { field: 'decision', headerName: 'Decision' },
              ]}
              rows={(dataState.state.data?.data || []).map((row) => ({
                id: row.id,
                reference: row.reference,
                scope: row.decision_scope,
                status: row.requires_review ? 'awaiting review' : row.allowed ? 'resolved' : 'failed',
                decision: row.decision_code,
              }))}
              emptyTitle="No policy decisions"
              emptyDescription="The user-scoped policy decisions query returned no rows."
            />
          ) : null}

          {section === 'beneficiaries' ? (
            <AdminDataTable
              title="Beneficiaries"
              description="Observed beneficiary classifications linked to policy decisions."
              columns={[
                { field: 'bankId', headerName: 'Bank ID' },
                { field: 'accountNumber', headerName: 'Account Number' },
                { field: 'classification', headerName: 'Classification' },
                { field: 'attemptCount', headerName: 'Attempts' },
                { field: 'status', headerName: 'Review State' },
              ]}
              rows={(dataState.state.data?.beneficiaries || []).map((row, index) => ({
                id: index + 1,
                bankId: row.bank_id,
                accountNumber: row.account_number,
                classification: row.classification,
                attemptCount: row.attempt_count,
                status: row.requires_review ? 'awaiting review' : 'resolved',
              }))}
              emptyTitle="No beneficiaries"
              emptyDescription="The beneficiaries endpoint returned no beneficiary observations."
            />
          ) : null}
        </RequestState>
      ) : (
        <AdminNoDataState title="Load a user ID" description="The refactored Users module is user-scoped. Enter a user ID to fetch controls, primary-account data, policy decisions, or beneficiaries." />
      )}
    </AdminScreenLayout>
  );
}

function SettlementOpsView() {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/queue')
    ? 'queue'
    : pathname.includes('/failed')
      ? 'failed'
      : pathname.includes('/stalled')
        ? 'stalled'
        : pathname.includes('/recovery-log')
          ? 'recovery'
          : 'overview';
  const overview = useAsyncState(null);
  const items = useAsyncState([]);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const loadOverview = async () => {
      overview.setLoading();
      try {
        const response = await fetchAdmin('/settlement/overview');
        overview.setData(response);
      } catch (error) {
        overview.setError(toErrorMessage(error));
      }
    };

    loadOverview();
  }, []);

  useEffect(() => {
    if (section === 'overview') {
      return;
    }

    const loadSection = async () => {
      items.setLoading();
      try {
        const route =
          section === 'queue'
            ? '/settlement/queue'
            : section === 'failed'
              ? '/settlement/failed'
              : section === 'stalled'
                ? '/settlement/stalled'
                : '/settlement/recovery_log';
        const response = await fetchAdmin(route);
        items.setData(response.items || []);
      } catch (error) {
        items.setError(toErrorMessage(error));
      }
    };

    loadSection();
  }, [section]);

  const completeAction = async () => {
    try {
      await postAdmin(pendingAction.path);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });
      const refreshRoute = section === 'queue' ? '/settlement/queue' : section === 'failed' ? '/settlement/failed' : '/settlement/stalled';
      const [refreshedSection, refreshedOverview] = await Promise.all([fetchAdmin(refreshRoute), fetchAdmin('/settlement/overview')]);
      items.setData(refreshedSection.items || []);
      overview.setData(refreshedOverview);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <AdminScreenLayout title="Settlement Ops" breadcrumbs={breadcrumbsFor('Settlement Ops')}>
      <RequestState loading={overview.state.loading} error={overview.state.error}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard title="Queued" value={overview.state.data?.queue || 0} caption="Settlement watches queued" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Failed" value={overview.state.data?.failed || 0} caption="Failed settlement watches" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Awaiting Review" value={overview.state.data?.awaiting_review || 0} caption="Review queues" />
          </Grid>
        </Grid>
      </RequestState>
      {section !== 'overview' ? (
        <RequestState loading={items.state.loading} error={items.state.error}>
          <AdminDataTable
            title={section === 'recovery' ? 'Recovery Log' : 'Settlement Watches'}
            description="Operational settlement visibility and recovery actions."
            columns={
              section === 'recovery'
                ? [
                    { field: 'watchId', headerName: 'Watch ID' },
                    { field: 'actionType', headerName: 'Action' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'actor', headerName: 'Actor' },
                  ]
                : [
                    { field: 'reference', headerName: 'Reference' },
                    { field: 'watchType', headerName: 'Watch Type' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'failureClass', headerName: 'Failure Class' },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      render: (row) => (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={() => setPendingAction({ path: `/settlement/${row.id}/retry`, success: 'Settlement retry accepted' })}>
                            Retry
                          </Button>
                          <Button size="small" onClick={() => setPendingAction({ path: `/settlement/${row.id}/requeue`, success: 'Settlement requeue accepted' })}>
                            Requeue
                          </Button>
                          <Button size="small" color="error" onClick={() => setPendingAction({ path: `/settlement/${row.id}/cancel`, success: 'Settlement cancelled' })}>
                            Cancel
                          </Button>
                          <Button size="small" color="warning" onClick={() => setPendingAction({ path: `/settlement/${row.id}/escalate`, success: 'Settlement escalated' })}>
                            Escalate
                          </Button>
                        </Stack>
                      ),
                    },
                  ]
            }
            rows={(items.state.data || []).map((row) =>
              section === 'recovery'
                ? {
                    id: row.id,
                    watchId: row.watch_id,
                    actionType: row.action_type,
                    status: row.status,
                    actor: row.actor_staff_id,
                  }
                : {
                    id: row.id,
                    reference: row.reference,
                    watchType: row.watch_type,
                    status: row.status,
                    failureClass: row.failure_class || 'N/A',
                  }
            )}
            emptyTitle="No settlement rows"
            emptyDescription="The selected settlement endpoint returned no operational rows."
          />
        </RequestState>
      ) : null}
      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm settlement action"
        description="Settlement retries, requeues, cancellations, and escalations are operationally sensitive."
        confirmLabel="Run Action"
        onClose={() => setPendingAction(null)}
        onConfirm={completeAction}
      />
    </AdminScreenLayout>
  );
}

function QueueJobsView() {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/queued')
    ? 'queued'
    : pathname.includes('/reserved')
      ? 'reserved'
      : pathname.includes('/failed')
        ? 'failed'
        : pathname.includes('/outbox')
          ? 'outbox'
          : 'overview';
  const overview = useAsyncState(null);
  const items = useAsyncState([]);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const loadOverview = async () => {
      overview.setLoading();
      try {
        const response = await fetchAdmin('/ops/queues/overview');
        overview.setData(response.overview || {});
      } catch (error) {
        overview.setError(toErrorMessage(error));
      }
    };

    loadOverview();
  }, []);

  useEffect(() => {
    if (section === 'overview') {
      return;
    }

    const load = async () => {
      items.setLoading();
      try {
        const route =
          section === 'queued'
            ? '/ops/queues/queued'
            : section === 'reserved'
              ? '/ops/queues/reserved'
              : section === 'failed'
                ? '/ops/queues/failed'
                : '/ops/queues/outbox';
        const response = await fetchAdmin(route);
        items.setData(response.items || []);
      } catch (error) {
        items.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section]);

  const completeAction = async () => {
    try {
      await postAdmin(pendingAction.path);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });
      const route =
        section === 'queued'
          ? '/ops/queues/queued'
          : section === 'reserved'
            ? '/ops/queues/reserved'
            : section === 'failed'
              ? '/ops/queues/failed'
              : '/ops/queues/outbox';
      const [refreshedItems, refreshedOverview] = await Promise.all([fetchAdmin(route), fetchAdmin('/ops/queues/overview')]);
      items.setData(refreshedItems.items || []);
      overview.setData(refreshedOverview.overview || {});
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <AdminScreenLayout title="Queue & Jobs" breadcrumbs={breadcrumbsFor('Queue & Jobs')}>
      <RequestState loading={overview.state.loading} error={overview.state.error}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard title="Queued Jobs" value={overview.state.data?.queued_jobs || 0} caption="Pending queue jobs" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Reserved Jobs" value={overview.state.data?.reserved_jobs || 0} caption="In-flight queue jobs" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Outbox Failed" value={overview.state.data?.outbox_failed || 0} caption="Dispatch intent failures" />
          </Grid>
        </Grid>
      </RequestState>
      {section !== 'overview' ? (
        <RequestState loading={items.state.loading} error={items.state.error}>
          <AdminDataTable
            title="Queue Jobs"
            description="Package queue jobs and outbox jobs remain clearly separated."
            columns={[
              { field: 'queue', headerName: section === 'outbox' ? 'Queue Name' : 'Queue' },
              { field: 'status', headerName: 'Status' },
              { field: 'details', headerName: 'Details' },
              {
                field: 'actions',
                headerName: 'Actions',
                render: (row) => (
                  <Stack direction="row" spacing={1}>
                    {section === 'failed' ? <Button size="small" onClick={() => setPendingAction({ path: `/ops/queues/failed/${row.id}/retry`, success: 'Failed job retry accepted' })}>Retry</Button> : null}
                    {section === 'queued' ? <Button size="small" color="error" onClick={() => setPendingAction({ path: `/ops/queues/queued/${row.id}/cancel`, success: 'Queued job cancelled' })}>Cancel</Button> : null}
                    {section === 'reserved' ? <Button size="small" onClick={() => setPendingAction({ path: `/ops/queues/reserved/${row.id}/release`, success: 'Reserved job released' })}>Release</Button> : null}
                    {section === 'outbox' ? (
                      <>
                        <Button size="small" onClick={() => setPendingAction({ path: `/ops/queues/outbox/${row.id}/retry`, success: 'Outbox retry accepted' })}>Retry</Button>
                        <Button size="small" color="error" onClick={() => setPendingAction({ path: `/ops/queues/outbox/${row.id}/cancel`, success: 'Outbox job cancelled' })}>Cancel</Button>
                      </>
                    ) : null}
                  </Stack>
                ),
              },
            ]}
            rows={(items.state.data || []).map((row) => ({
              id: row.id,
              queue: row.queue || row.queue_name,
              status: section === 'outbox' ? row.status : row.status === 1 ? 'reserved' : row.status === 0 ? 'queued' : 'failed',
              details: section === 'outbox' ? row.dedupe_key : row.priority || row.connection,
            }))}
            emptyTitle="No jobs"
            emptyDescription="The selected queue endpoint returned no job rows."
          />
        </RequestState>
      ) : null}
      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm queue action"
        description="Queue actions operate directly on active queue or outbox records."
        confirmLabel="Run Action"
        onClose={() => setPendingAction(null)}
        onConfirm={completeAction}
      />
    </AdminScreenLayout>
  );
}

function ConfigView() {
  const { pathname } = useLocation();
  const { groupKey } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/feature-flags') ? 'flags' : pathname.includes('/audit') ? 'audit' : groupKey ? 'group' : 'groups';
  const groups = useAsyncState({ groups: [], feature_flags: [] });
  const groupDetail = useAsyncState(null);
  const audit = useAsyncState([]);

  useEffect(() => {
    const load = async () => {
      groups.setLoading();
      try {
        const response = await fetchAdmin('/config/groups');
        groups.setData(response);
      } catch (error) {
        groups.setError(toErrorMessage(error));
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (section !== 'group' || !groupKey) {
      return;
    }

    const load = async () => {
      groupDetail.setLoading();
      try {
        const response = await fetchAdmin(`/config/groups/${groupKey}`);
        groupDetail.setData(response);
      } catch (error) {
        groupDetail.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section, groupKey]);

  useEffect(() => {
    if (section !== 'audit') {
      return;
    }

    const load = async () => {
      audit.setLoading();
      try {
        const response = await fetchAdmin('/config/audit');
        audit.setData(response.changes || []);
      } catch (error) {
        audit.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section]);

  const updateItem = async (item) => {
    try {
      await putAdmin(`/config/items/${item.id}`, { value: item.value, reason: item.reason || '' });
      enqueueSnackbar('Config item updated', { variant: 'success' });
      const refreshed = await fetchAdmin(`/config/groups/${groupKey}`);
      groupDetail.setData(refreshed);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const updateFlag = async (flag) => {
    try {
      await putAdmin(`/config/feature_flags/${flag.flag_key}`, { is_enabled: !Boolean(flag.is_enabled) });
      enqueueSnackbar('Feature flag updated', { variant: 'success' });
      const refreshed = await fetchAdmin('/config/groups');
      groups.setData(refreshed);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const rollback = async (logId) => {
    try {
      await postAdmin(`/config/rollback/${logId}`);
      enqueueSnackbar('Config rollback applied', { variant: 'success' });
      const refreshed = await fetchAdmin('/config/audit');
      audit.setData(refreshed.changes || []);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <AdminScreenLayout title="Config" breadcrumbs={breadcrumbsFor('Config')}>
      {section === 'groups' ? (
        <RequestState loading={groups.state.loading} error={groups.state.error}>
          <AdminDataTable
            title="Config Groups"
            description="Groups plus feature flags returned from the grouped config endpoint."
            columns={[
              { field: 'groupKey', headerName: 'Group Key' },
              { field: 'label', headerName: 'Label' },
              { field: 'description', headerName: 'Description' },
            ]}
            rows={(groups.state.data?.groups || []).map((row) => ({
              id: row.id,
              groupKey: row.group_key,
              label: row.label,
              description: row.description,
            }))}
            emptyTitle="No config groups"
            emptyDescription="The config groups endpoint returned no groups."
          />
        </RequestState>
      ) : null}

      {section === 'group' ? (
        <RequestState loading={groupDetail.state.loading} error={groupDetail.state.error}>
          <Stack spacing={3}>
            <AdminDetailPanel
              title="Resolved Group State"
              items={Object.entries(groupDetail.state.data?.resolved || {}).map(([key, value]) => ({
                label: key,
                value: Array.isArray(value) ? value.join(', ') : String(value),
              }))}
            />
            {(groupDetail.state.data?.items || []).map((item) => (
              <Card variant="outlined" key={item.id}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6">{item.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                    <TextField
                      label="Value"
                      defaultValue={item.value}
                      onBlur={(event) => {
                        item.value = event.target.value;
                      }}
                    />
                    <TextField
                      label="Reason"
                      defaultValue=""
                      onBlur={(event) => {
                        item.reason = event.target.value;
                      }}
                    />
                    <LoadingButton variant="contained" onClick={() => updateItem(item)}>
                      Save Item
                    </LoadingButton>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </RequestState>
      ) : null}

      {section === 'flags' ? (
        <RequestState loading={groups.state.loading} error={groups.state.error}>
          <AdminDataTable
            title="Feature Flags"
            description="Current flag state from the config groups payload."
            columns={[
              { field: 'flagKey', headerName: 'Flag Key' },
              { field: 'label', headerName: 'Label' },
              { field: 'status', headerName: 'Status' },
              {
                field: 'actions',
                headerName: 'Actions',
                render: (row) => (
                  <Button size="small" onClick={() => updateFlag(row.original)}>
                    Toggle
                  </Button>
                ),
              },
            ]}
            rows={(groups.state.data?.feature_flags || []).map((row) => ({
              id: row.id,
              original: row,
              flagKey: row.flag_key,
              label: row.label,
              status: row.is_enabled ? 'active' : 'cancelled',
            }))}
            emptyTitle="No feature flags"
            emptyDescription="The config endpoint returned no feature flags."
          />
        </RequestState>
      ) : null}

      {section === 'audit' ? (
        <RequestState loading={audit.state.loading} error={audit.state.error}>
          <AdminDataTable
            title="Config Audit"
            description="Change history plus rollback actions."
            columns={[
              { field: 'groupKey', headerName: 'Group' },
              { field: 'itemKey', headerName: 'Item' },
              { field: 'changedBy', headerName: 'Changed By' },
              { field: 'createdAt', headerName: 'Changed At' },
              {
                field: 'actions',
                headerName: 'Actions',
                render: (row) => (
                  <Button size="small" onClick={() => rollback(row.id)}>
                    Rollback
                  </Button>
                ),
              },
            ]}
            rows={(audit.state.data || []).map((row) => ({
              id: row.id,
              groupKey: row.group_key,
              itemKey: row.item_key,
              changedBy: row.changed_by,
              createdAt: fDateTime(row.created_at),
            }))}
            emptyTitle="No config audit rows"
            emptyDescription="The config audit endpoint returned no change history."
          />
        </RequestState>
      ) : null}
    </AdminScreenLayout>
  );
}

function AdjustmentsView() {
  const { requestId } = useParams();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const section = pathname.includes('/create') ? 'create' : requestId ? 'detail' : 'list';
  const list = useAsyncState([]);
  const detail = useAsyncState(null);
  const [form, setForm] = useState({
    target_type: 'user',
    user_id: '',
    wallet_type: 'fiat',
    asset_code: 'NGN',
    direction: 'credit',
    amount: '',
    reason_code: 'manual_correction',
    reason_note: '',
    reference_type: '',
    reference_id: '',
  });

  useEffect(() => {
    if (section !== 'list') {
      return;
    }

    const load = async () => {
      list.setLoading();
      try {
        const response = await fetchAdmin('/adjustments');
        list.setData(response.items || []);
      } catch (error) {
        list.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section]);

  useEffect(() => {
    if (section !== 'detail' || !requestId) {
      return;
    }

    const load = async () => {
      detail.setLoading();
      try {
        const response = await fetchAdmin(`/adjustments/${requestId}`);
        detail.setData(response);
      } catch (error) {
        detail.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section, requestId]);

  const createRequest = async () => {
    try {
      await postAdmin('/adjustments', form);
      enqueueSnackbar('Adjustment request created', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const runAction = async (action) => {
    try {
      await postAdmin(`/adjustments/${requestId}/${action}`);
      enqueueSnackbar(`Adjustment ${action} accepted`, { variant: 'success' });
      const refreshed = await fetchAdmin(`/adjustments/${requestId}`);
      detail.setData(refreshed);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <AdminScreenLayout title="Adjustments" breadcrumbs={breadcrumbsFor('Adjustments')}>
      {section === 'list' ? (
        <RequestState loading={list.state.loading} error={list.state.error}>
          <AdminDataTable
            title="Adjustment Requests"
            description="Maker-checker adjustment requests and statuses."
            columns={[
              { field: 'requestId', headerName: 'Request ID' },
              { field: 'userId', headerName: 'User ID' },
              { field: 'status', headerName: 'Status' },
              { field: 'amount', headerName: 'Amount' },
            ]}
            rows={(list.state.data || []).map((row) => ({
              id: row.id,
              requestId: row.request_id,
              userId: row.user_id,
              status: row.status,
              amount: `${row.asset_code} ${row.amount}`,
            }))}
            emptyTitle="No adjustments"
            emptyDescription="The adjustments endpoint returned no request rows."
          />
        </RequestState>
      ) : null}

      {section === 'create' ? (
        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={2}>
              {Object.entries(form).map(([key, value]) => (
                <Grid item xs={12} md={6} key={key}>
                  <TextField fullWidth label={key} value={value} onChange={(event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))} />
                </Grid>
              ))}
            </Grid>
            <LoadingButton sx={{ mt: 3 }} variant="contained" onClick={createRequest}>
              Create Adjustment Request
            </LoadingButton>
          </CardContent>
        </Card>
      ) : null}

      {section === 'detail' ? (
        <RequestState loading={detail.state.loading} error={detail.state.error}>
          {detail.state.data ? (
            <Stack spacing={3}>
              <AdminDetailPanel
                title="Adjustment Request"
                items={[
                  { label: 'Request ID', value: detail.state.data.request?.request_id || requestId },
                  { label: 'Status', value: detail.state.data.request?.status || 'N/A' },
                  { label: 'Approved by', value: detail.state.data.request?.approved_by || 'N/A' },
                ]}
              />
              <AdminDataTable
                title="Approvals"
                description="Approval history for the current adjustment request."
                columns={[
                  { field: 'stage', headerName: 'Stage' },
                  { field: 'staffId', headerName: 'Staff ID' },
                  { field: 'decision', headerName: 'Decision' },
                ]}
                rows={(detail.state.data.approvals || []).map((row) => ({
                  id: row.id,
                  stage: row.approval_stage,
                  staffId: row.staff_id,
                  decision: row.decision,
                }))}
                emptyTitle="No approvals"
                emptyDescription="No approval history exists for the selected request yet."
              />
              <AdminTimeline
                title="Adjustment Events"
                items={(detail.state.data.events || []).map((row) => ({
                  id: `${row.id}`,
                  title: row.event_type,
                  description: JSON.stringify(safeJsonParse(row.payload, {})),
                  time: fDateTime(row.created_at),
                  color: row.event_type === 'executed' ? 'success' : 'primary',
                }))}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => runAction('approve')}>Approve</Button>
                <Button variant="outlined" color="error" onClick={() => runAction('reject')}>Reject</Button>
                <Button variant="outlined" onClick={() => runAction('execute')}>Execute</Button>
                <Button variant="outlined" color="warning" onClick={() => runAction('cancel')}>Cancel</Button>
              </Stack>
            </Stack>
          ) : null}
        </RequestState>
      ) : null}
    </AdminScreenLayout>
  );
}

function ServicesView() {
  const { pathname } = useLocation();
  const { provider } = useParams();
  const section = provider ? 'provider' : pathname.includes('/incidents') ? 'incidents' : pathname.includes('/blockchain') ? 'blockchain' : 'overview';
  const overview = useAsyncState(null);
  const detail = useAsyncState(null);

  useEffect(() => {
    const load = async () => {
      if (section === 'provider' && provider) {
        detail.setLoading();
        try {
          const response = await fetchAdmin(`/services/provider/${provider}`);
          detail.setData(response);
        } catch (error) {
          detail.setError(toErrorMessage(error));
        }
        return;
      }

      const route =
        section === 'incidents'
          ? '/services/incidents'
          : section === 'blockchain'
            ? '/services/blockchain'
            : '/services/overview';
      overview.setLoading();
      try {
        const response = await fetchAdmin(route);
        overview.setData(response);
      } catch (error) {
        overview.setError(toErrorMessage(error));
      }
    };

    load();
  }, [section, provider]);

  return (
    <AdminScreenLayout title="Service Reports" breadcrumbs={breadcrumbsFor('Service Reports')}>
      {section === 'overview' ? (
        <RequestState loading={overview.state.loading} error={overview.state.error}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <AdminDataTable
                title="Provider Metrics"
                description="Service provider report snapshots."
                columns={[
                  { field: 'provider', headerName: 'Provider' },
                  { field: 'successRate', headerName: 'Success Rate' },
                  { field: 'requests', headerName: 'Request Count' },
                  { field: 'latency', headerName: 'P95 Latency' },
                ]}
                rows={(overview.state.data?.providers || []).map((row) => ({
                  id: row.id,
                  provider: row.provider,
                  successRate: `${row.success_rate}%`,
                  requests: row.request_count,
                  latency: `${row.p95_latency_ms} ms`,
                }))}
                emptyTitle="No provider metrics"
                emptyDescription="The provider overview endpoint returned no report rows."
              />
            </Grid>
            <Grid item xs={12} lg={5}>
              <AdminDataTable
                title="Current Incidents"
                description="Open or recent incidents from provider monitoring."
                columns={[
                  { field: 'provider', headerName: 'Provider' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'severity', headerName: 'Severity' },
                ]}
                rows={(overview.state.data?.incidents || []).map((row) => ({
                  id: row.id,
                  provider: row.provider,
                  status: row.status,
                  severity: row.severity,
                }))}
                emptyTitle="No incidents"
                emptyDescription="The overview endpoint returned no incidents."
              />
            </Grid>
          </Grid>
        </RequestState>
      ) : null}

      {section === 'provider' ? (
        <RequestState loading={detail.state.loading} error={detail.state.error}>
          {detail.state.data ? (
            <Stack spacing={3}>
              <AdminDataTable
                title="Provider Logs"
                description="Request and response logs for the selected provider."
                columns={[
                  { field: 'endpoint', headerName: 'Endpoint' },
                  { field: 'statusCode', headerName: 'Status Code' },
                  { field: 'createdAt', headerName: 'Created' },
                ]}
                rows={(detail.state.data.logs || []).map((row) => ({
                  id: row.id,
                  endpoint: row.endpoint,
                  statusCode: row.status_code,
                  createdAt: fDateTime(row.created_at),
                }))}
                emptyTitle="No logs"
                emptyDescription="The provider detail endpoint returned no request logs."
              />
              <AdminDataTable
                title="Provider Snapshots"
                description="Snapshot metrics for the selected provider."
                columns={[
                  { field: 'reportDate', headerName: 'Report Date' },
                  { field: 'successRate', headerName: 'Success Rate' },
                  { field: 'latency', headerName: 'P95 Latency' },
                ]}
                rows={(detail.state.data.snapshots || []).map((row) => ({
                  id: row.id,
                  reportDate: row.report_date,
                  successRate: `${row.success_rate}%`,
                  latency: `${row.p95_latency_ms} ms`,
                }))}
                emptyTitle="No snapshots"
                emptyDescription="The provider detail endpoint returned no snapshot rows."
              />
            </Stack>
          ) : null}
        </RequestState>
      ) : null}

      {section === 'incidents' ? (
        <RequestState loading={overview.state.loading} error={overview.state.error}>
          <AdminDataTable
            title="Incidents"
            description="Provider incidents returned from the incidents endpoint."
            columns={[
              { field: 'provider', headerName: 'Provider' },
              { field: 'status', headerName: 'Status' },
              { field: 'severity', headerName: 'Severity' },
              { field: 'summary', headerName: 'Summary' },
            ]}
            rows={(overview.state.data?.incidents || []).map((row) => ({
              id: row.id,
              provider: row.provider,
              status: row.status,
              severity: row.severity,
              summary: row.summary,
            }))}
            emptyTitle="No incidents"
            emptyDescription="The incidents endpoint returned no incident rows."
          />
        </RequestState>
      ) : null}

      {section === 'blockchain' ? (
        <RequestState loading={overview.state.loading} error={overview.state.error}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <AdminDataTable
                title="Blockchain Cases"
                description="Service-visible blockchain or settlement exception cases."
                columns={[
                  { field: 'caseType', headerName: 'Case Type' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'resource', headerName: 'Resource' },
                ]}
                rows={(overview.state.data?.cases || []).map((row) => ({
                  id: row.id,
                  caseType: row.case_type,
                  status: row.status,
                  resource: `${row.resource_type}:${row.resource_id}`,
                }))}
                emptyTitle="No blockchain cases"
                emptyDescription="The blockchain visibility endpoint returned no cases."
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AdminDataTable
                title="Blockchain Transactions"
                description="Observed blockchain transactions from the services/blockchain or blockchain ops alias endpoint."
                columns={[
                  { field: 'reference', headerName: 'Reference' },
                  { field: 'asset', headerName: 'Asset' },
                  { field: 'network', headerName: 'Network' },
                  { field: 'status', headerName: 'Status' },
                ]}
                rows={(overview.state.data?.transactions || []).map((row) => ({
                  id: row.id,
                  reference: row.reference,
                  asset: row.asset,
                  network: row.network,
                  status: row.status,
                }))}
                emptyTitle="No blockchain transactions"
                emptyDescription="The blockchain visibility endpoint returned no transaction rows."
              />
            </Grid>
          </Grid>
        </RequestState>
      ) : null}
    </AdminScreenLayout>
  );
}

export default function LiveAdminModulePage({ moduleKey }) {
  switch (moduleKey) {
    case 'dashboard':
      return <DashboardView />;
    case 'accessControl':
      return <AccessControlView />;
    case 'riskPolicy':
      return <RiskPolicyView />;
    case 'users':
      return <UsersView />;
    case 'transactions':
      return <TransactionsView title="Transactions" breadcrumbs={breadcrumbsFor('Transactions')} />;
    case 'settlementOps':
      return <SettlementOpsView />;
    case 'queueJobs':
      return <QueueJobsView />;
    case 'config':
      return <ConfigView />;
    case 'adjustments':
      return <AdjustmentsView />;
    case 'serviceReports':
      return <ServicesView />;
    default:
      return null;
  }
}
